import { useFlag } from '@reflag/react-sdk';
import type * as React from 'react';

import * as api from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { SegmentedControl, type SegmentedControlOption } from '@gitbook/spine-react';

import { createPickConversationsIssuesClusterPalette } from '../Conversations';
import { createPickOrganizationMemberPalette } from '../Search';
import { useSelectSpacePalette } from '../Space';

export type ChangeRequestsListFilterState = {
    status?: api.ChangeRequestStatus;
    contributor?: string;
    requestedReview?: string;
    creator?: string;
    space?: string;
    conversationsIssuesCluster?: string;
};

const statusOptions: Array<SegmentedControlOption<api.ChangeRequestStatus>> = [
    { value: api.ChangeRequestStatus.Draft, label: 'Draft' },
    { value: api.ChangeRequestStatus.Open, label: 'In review' },
    { value: api.ChangeRequestStatus.Merged, label: 'Merged' },
    { value: api.ChangeRequestStatus.Archived, label: 'Archived' },
];

/**
 * UI to filter the list of change requests.
 */
export function ChangeRequestsListFilter(props: {
    organizationId: string;
    filters: ChangeRequestsListFilterState;
    onFiltersChange: (filters: ChangeRequestsListFilterState) => void;

    /**
     * If false, the user will not be able to filter by space.
     */
    withSpaceFilter?: boolean;

    /**
     * The size of the filter.
     */
    size?: 'small' | 'medium';
}): React.ReactElement {
    const {
        organizationId,
        filters,
        onFiltersChange,
        withSpaceFilter = true,
        size = 'medium',
    } = props;

    const featureDocsAgent = useFlag('DOCS_AGENTS');

    const pickSpacePalette = useSelectSpacePalette({
        organizationId,
        selectedSpaceId: filters.space,
        onSpaceSelected: (space) => onFiltersChange({ ...filters, space: space.id }),
    });

    const hasFilters =
        !!filters.contributor ||
        !!filters.space ||
        !!filters.creator ||
        !!filters.requestedReview ||
        !!filters.conversationsIssuesCluster;

    return (
        <div className="flex flex-1 items-center gap-2">
            <div className="flex-1">
                <SegmentedControl
                    active={filters.status ?? api.ChangeRequestStatus.Open}
                    options={statusOptions}
                    onChange={(status) =>
                        onFiltersChange({
                            ...filters,
                            status: status,
                        })
                    }
                    size={size}
                />
            </div>
            <PaletteButton
                label="Filter change requests by"
                leadingIcon={Icon.Filter}
                kind="blank"
                iconOnly
                palette={() =>
                    createPalette({
                        groups: [
                            {
                                key: 'filters',
                                commands: [
                                    {
                                        key: 'creator',
                                        title: 'Filter by creator',
                                        icon: Icon.User,
                                        descendant: () =>
                                            createPickOrganizationMemberPalette({
                                                organizationId,
                                                selectedUserId: filters.creator,
                                                suggestGitBookAgent: featureDocsAgent.isEnabled,
                                                onPick: (userId) =>
                                                    onFiltersChange({
                                                        ...filters,
                                                        creator: userId,
                                                    }),
                                            }),
                                    },
                                    {
                                        key: 'contributor',
                                        title: 'Filter by participant',
                                        icon: Icon.Users,
                                        descendant: () =>
                                            createPickOrganizationMemberPalette({
                                                organizationId,
                                                selectedUserId: filters.contributor,
                                                suggestGitBookAgent: featureDocsAgent.isEnabled,
                                                onPick: (userId) =>
                                                    onFiltersChange({
                                                        ...filters,
                                                        contributor: userId,
                                                    }),
                                            }),
                                    },
                                    {
                                        key: 'requestedReview',
                                        title: 'Filter by awaited review',
                                        icon: Icon.RoleReviewer,
                                        descendant: () =>
                                            createPickOrganizationMemberPalette({
                                                organizationId,
                                                selectedUserId: filters.requestedReview,
                                                onPick: (userId) =>
                                                    onFiltersChange({
                                                        ...filters,
                                                        requestedReview: userId,
                                                    }),
                                            }),
                                    },
                                    ...(withSpaceFilter
                                        ? [
                                              {
                                                  key: 'space',
                                                  title: 'Filter by space',
                                                  icon: Icon.Space,
                                                  descendant: pickSpacePalette,
                                              },
                                          ]
                                        : []),
                                    ...(featureDocsAgent.isEnabled
                                        ? [
                                              {
                                                  key: 'conversationsIssuesCluster',
                                                  title: 'Filter by conversations topic',
                                                  icon: Icon.CommentThread,
                                                  descendant: () =>
                                                      createPickConversationsIssuesClusterPalette({
                                                          organizationId,
                                                          selectedClusterId:
                                                              filters.conversationsIssuesCluster,
                                                          onPick: (clusterId) => {
                                                              onFiltersChange({
                                                                  ...filters,
                                                                  conversationsIssuesCluster:
                                                                      clusterId,
                                                              });
                                                          },
                                                      }),
                                              },
                                          ]
                                        : []),
                                ],
                            },
                            ...(hasFilters
                                ? [
                                      {
                                          key: 'reset',
                                          commands: [
                                              {
                                                  key: 'clear',
                                                  title: 'Clear filters',
                                                  icon: Icon.Trashcan,
                                                  trigger: () => {
                                                      onFiltersChange({
                                                          ...filters,
                                                          contributor: undefined,
                                                          space: undefined,
                                                          requestedReview: undefined,
                                                          creator: undefined,
                                                          conversationsIssuesCluster: undefined,
                                                      });
                                                  },
                                              },
                                          ],
                                      },
                                  ]
                                : []),
                        ],
                    })
                }
                dot={hasFilters ? 'active' : undefined}
            />
        </div>
    );
}
